import React from 'react';
import MedicalTerm from './MedicalTerm';
import { medicalTerms, getMedicalTermsList } from '../data/medicalGlossary';

// Automatically detect and wrap medical terms in text with tooltips
const MedicalText = ({ children, className = '' }) => {
  if (typeof children !== 'string') {
    return <span className={className}>{children}</span>;
  }

  const text = children;
  const termsList = getMedicalTermsList();
  
  // Create a regex pattern that matches whole words only (case-insensitive)
  const pattern = new RegExp(
    `\\b(${termsList.map(t => t.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|')})\\b`,
    'gi'
  );

  const parts = [];
  let lastIndex = 0;
  let match;
  const usedTerms = new Set(); // Track which terms we've already tooltipped

  // Reset regex state
  pattern.lastIndex = 0;

  while ((match = pattern.exec(text)) !== null) {
    const matchedTerm = match[0];
    const lowerTerm = matchedTerm.toLowerCase();
    
    // Only add tooltip for first occurrence of each term
    if (usedTerms.has(lowerTerm)) {
      continue;
    }
    
    const termInfo = medicalTerms[lowerTerm];
    if (!termInfo) continue;

    // Add text before the match
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index));
    }

    // Add the medical term with tooltip
    parts.push(
      <MedicalTerm
        key={`${lowerTerm}-${match.index}`}
        term={termInfo.term}
        definition={termInfo.definition}
        anatomyPage={termInfo.anatomyPage}
      >
        {matchedTerm}
      </MedicalTerm>
    );

    usedTerms.add(lowerTerm);
    lastIndex = match.index + matchedTerm.length;
  }

  // Add remaining text
  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }

  // If no terms found, return original text
  if (parts.length === 0) {
    return <span className={className}>{text}</span>;
  }

  return <span className={className}>{parts}</span>;
};

// Process markdown/HTML content and add tooltips
export const processMedicalContent = (content) => {
  if (!content) return content;
  
  const termsList = getMedicalTermsList();
  let processedContent = content;
  const usedTerms = new Set();

  termsList.forEach(term => {
    if (usedTerms.has(term)) return;
    
    const termInfo = medicalTerms[term];
    if (!termInfo) return;

    // Create regex for whole word match (case-insensitive)
    const regex = new RegExp(`\\b(${term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})\\b`, 'i');
    const match = processedContent.match(regex);
    
    if (match) {
      // Only replace first occurrence
      const tooltip = `<span class="has-tooltip" data-term="${termInfo.term}" data-definition="${termInfo.definition}" data-anatomy="${termInfo.anatomyPage || ''}">${match[0]}</span>`;
      processedContent = processedContent.replace(regex, tooltip);
      usedTerms.add(term);
    }
  });

  return processedContent;
};

export default MedicalText;
